import { z } from 'zod';
import { BaseEventSchema } from './Base';
import { ProductInteractionLocation } from './Product';

const ScrollEventSchema = BaseEventSchema.extend({
  scroll_percentage: z.number(),
});

const LandingEventSchema = BaseEventSchema.extend({
  referral_link: z.union([z.string(), z.array(z.string())]).optional(),
  logged: z.boolean(),
});

const ProductViewEventSchema = BaseEventSchema.extend({
  product_id: z.number(),
  product_name: z.string(),
  product_price: z.number().optional(),
  product_prescription_type: z.string().optional().nullable(),
  product_brand: z.string(),
  product_availability: z.string(),
  access: z
    .union([z.nativeEnum(ProductInteractionLocation), z.string()])
    .nullable(),
});

const NewAddressViewSchema = BaseEventSchema.extend({
  screen_version: z.union([
    z.literal('form'),
    z.literal('map'),
    z.literal('confirmation'),
  ]),
});

const ScreenDeliveryAddressViewSchema = BaseEventSchema.extend({
  address_quantity: z.number(),
});

const ScreenPrescriptionsViewSchema = BaseEventSchema.extend({
  prescriptions_required: z.boolean(),
  prescriptions_needed: z.boolean(),
  highest_prescription_type: z.string(),
});

export enum ScreenEventType {
  // Session
  ScreenLogIn = 'screen.session.login.view',
  ScreenVerificationCode = 'screen.session.verification_code.view',
  ScreenBiometricCode = 'screen.session.biometric.view',
  ScreenUserInfo = 'screen.session.signin_user_info.view',
  ScreenSourceInfo = 'screen.session.signin_source_info.view',
  // General Screens
  ScreenHomeView = 'screen.home.view',
  ScreenHomeScroll = 'screen.home.scroll',
  ScreenLandingView = 'screen.landing.view',
  ScreenLandingScroll = 'screen.landing.scroll',
  ScreenResultsView = 'screen.results.view',
  ScreenProductView = 'screen.product.view',
  ScreenLastWhimView = 'screen.last_whim.view',
  ScreenMyOrdersView = 'screen.my_orders.view',
  ScreenMyPrescriptionsView = 'screen.my_prescriptions.view',
  ScreenMyAddressesView = 'screen.my_addresses.view',
  ScreenMyPaymentMethodsView = 'screen.my_payment_methods.view',
  ScreenMyUserInfoView = 'screen.my_user_info.view',
  ScreenMyInsurancesView = 'screen.my_insurances.view',
  ScreenNewAddressView = 'screen.new_address.view',
  ScreenUserInfoView = 'screen.signin_user_info.view',
  ScreenSourceInfoView = 'screen.signin_source_info.view',
  ScreenMekiDocCartView = 'screen.mekidoc_cart.view',
  // Checkout
  ScreenCheckoutDeliveryAddressView = 'screen.checkout.delivery_address.view',
  ScreenCheckoutDeliveryDateView = 'screen.checkout.delivery_date.view',
  ScreenCheckoutPaymentMethodView = 'screen.checkout.payment_method.view',
  ScreenCheckoutInstallmentsView = 'screen.checkout.installments.view',
  ScreenCheckoutPrescriptionsView = 'screen.checkout.prescriptions.view',
  ScreenCheckoutConfirmPaymentView = 'screen.checkout.confirm_payment.view',
  ScreenCheckoutPaymentSuccessView = 'screen.checkout.payment_success.view',
  // Errors
  ScreenSearchErrorView = 'screen.search_error.view',
  ScreenGenericErrorView = 'screen.generic_error.view',
}

export const ScreenEventTrackingSchemas = {
  [ScreenEventType.ScreenLogIn]: BaseEventSchema,
  [ScreenEventType.ScreenVerificationCode]: BaseEventSchema,
  [ScreenEventType.ScreenBiometricCode]: BaseEventSchema,
  [ScreenEventType.ScreenUserInfo]: BaseEventSchema,
  [ScreenEventType.ScreenSourceInfo]: BaseEventSchema,
  [ScreenEventType.ScreenHomeView]: BaseEventSchema,
  [ScreenEventType.ScreenHomeScroll]: ScrollEventSchema,
  [ScreenEventType.ScreenLandingView]: LandingEventSchema,
  [ScreenEventType.ScreenLandingScroll]: ScrollEventSchema,
  [ScreenEventType.ScreenResultsView]: BaseEventSchema,
  [ScreenEventType.ScreenProductView]: ProductViewEventSchema,
  [ScreenEventType.ScreenLastWhimView]: BaseEventSchema,
  [ScreenEventType.ScreenMyOrdersView]: BaseEventSchema,
  [ScreenEventType.ScreenMyPrescriptionsView]: BaseEventSchema,
  [ScreenEventType.ScreenMyAddressesView]: BaseEventSchema,
  [ScreenEventType.ScreenMyPaymentMethodsView]: BaseEventSchema,
  [ScreenEventType.ScreenMyUserInfoView]: BaseEventSchema,
  [ScreenEventType.ScreenMyInsurancesView]: BaseEventSchema,
  [ScreenEventType.ScreenNewAddressView]: NewAddressViewSchema,
  [ScreenEventType.ScreenCheckoutDeliveryAddressView]:
    ScreenDeliveryAddressViewSchema,
  [ScreenEventType.ScreenMekiDocCartView]: BaseEventSchema,
  [ScreenEventType.ScreenCheckoutDeliveryDateView]: BaseEventSchema,
  [ScreenEventType.ScreenCheckoutPaymentMethodView]: BaseEventSchema,
  [ScreenEventType.ScreenCheckoutInstallmentsView]: BaseEventSchema,
  [ScreenEventType.ScreenCheckoutPrescriptionsView]:
    ScreenPrescriptionsViewSchema,
  [ScreenEventType.ScreenCheckoutConfirmPaymentView]: BaseEventSchema,
  [ScreenEventType.ScreenCheckoutPaymentSuccessView]: BaseEventSchema,
  [ScreenEventType.ScreenSearchErrorView]: BaseEventSchema,
  [ScreenEventType.ScreenGenericErrorView]: BaseEventSchema,
} as const;
